/**
 * ResponsiveTable — renders as a standard MUI Table on desktop and as a
 * stack of cards on mobile (xs). Zero horizontal scroll on phones.
 *
 * Desktop: sortable columns (when `column.sortable` is true) + optional row
 * selection (when `selectable` is true). Mobile: cards are selectable via
 * long-press/tap when `selectable` is on, and headers still show an
 * "Select all" affordance at the top.
 *
 * Usage:
 *   <ResponsiveTable
 *     columns={[
 *       { key: 'title',    label: 'Title',    primary: true, sortable: true },
 *       { key: 'status',   label: 'Status',   sortable: true, render: (row) => <Chip label={row.status}/> },
 *       { key: 'priority', label: 'Priority', hideOnMobile: true, sortable: true },
 *       ...
 *     ]}
 *     rows={tickets}
 *     rowKey={(row) => row.id}
 *     onRowClick={(row) => openTicket(row)}
 *     emptyMessage="No tickets yet"
 *
 *     // Sort (controlled):
 *     sortBy="status"
 *     sortDir="asc"
 *     onSortChange={({ sortBy, sortDir }) => setSort(...)}
 *
 *     // Selection (controlled):
 *     selectable
 *     selectedIds={[1,2,3]}
 *     onSelectionChange={(ids) => setSelected(ids)}
 *   />
 *
 * Column fields:
 *   - key:           data key (used for sort, React key)
 *   - label:         column header label
 *   - render(row):   optional custom cell renderer; if omitted uses row[key]
 *   - sortable:      make this column clickable to sort
 *   - primary:       mobile card's headline (usually title/name). Max 1 primary.
 *   - secondary:     mobile card's sub-headline (e.g., ticket number). Max 1 secondary.
 *   - hideOnMobile:  skip this column entirely in the mobile card
 *   - align:         'left' | 'right' | 'center' (desktop table only)
 *   - mobileOrder:   number — order in mobile card (smaller = higher)
 */

import React from 'react';
import {
  Box, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TableSortLabel, Typography, useMediaQuery, useTheme, Stack, Checkbox,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export default function ResponsiveTable({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyMessage = 'No records found',
  density = 'small',
  // Sorting (controlled):
  sortBy,
  sortDir = 'asc',
  onSortChange,
  // Selection (controlled):
  selectable = false,
  selectedIds = [],
  onSelectionChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const selectedSet = React.useMemo(
    () => new Set((selectedIds || []).map(String)),
    [selectedIds]
  );

  const toggleRow = (rowId) => {
    if (!onSelectionChange) return;
    const key = String(rowId);
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSelectionChange(Array.from(next).map(x => {
      // Preserve numeric ids when possible
      const n = Number(x);
      return Number.isFinite(n) && String(n) === x ? n : x;
    }));
  };

  const toggleAll = () => {
    if (!onSelectionChange || !rows) return;
    const allIds = rows.map(r => rowKey(r));
    const allSelected = allIds.every(id => selectedSet.has(String(id)));
    onSelectionChange(allSelected ? [] : allIds);
  };

  const handleSort = (col) => {
    if (!col.sortable || !onSortChange) return;
    const nextDir = sortBy === col.key && sortDir === 'asc' ? 'desc' : 'asc';
    onSortChange({ sortBy: col.key, sortDir: nextDir });
  };

  if (!rows || rows.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Paper>
    );
  }

  const allIds = rows.map(r => rowKey(r));
  const allSelected = selectable && allIds.length > 0 && allIds.every(id => selectedSet.has(String(id)));
  const someSelected = selectable && !allSelected && allIds.some(id => selectedSet.has(String(id)));

  // ── Mobile card layout ──────────────────────────────────────────────────
  if (isMobile) {
    const primaryCol   = columns.find(c => c.primary);
    const secondaryCol = columns.find(c => c.secondary);
    const otherCols = columns
      .filter(c => !c.primary && !c.secondary && !c.hideOnMobile)
      .sort((a, b) => (a.mobileOrder ?? 99) - (b.mobileOrder ?? 99));

    return (
      <Stack spacing={1}>
        {selectable && (
          <Paper variant="outlined" sx={{ px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleAll}
            />
            <Typography variant="caption" color="text.secondary">
              {selectedIds.length ? `${selectedIds.length} selected` : 'Select all'}
            </Typography>
          </Paper>
        )}
        {rows.map((row) => {
          const id = rowKey(row);
          const isSelected = selectable && selectedSet.has(String(id));
          return (
            <Paper
              key={id}
              variant="outlined"
              onClick={(e) => {
                // Ignore clicks that originate from the checkbox
                if (e.target && e.target.closest && e.target.closest('.rt-select-cell')) return;
                if (onRowClick) onRowClick(row);
              }}
              sx={{
                p: 1.5,
                cursor: onRowClick ? 'pointer' : 'default',
                '&:active': onRowClick ? { bgcolor: 'action.hover' } : {},
                borderRadius: 2,
                borderColor: isSelected ? 'primary.main' : undefined,
                bgcolor: isSelected ? 'action.selected' : undefined,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {selectable && (
                  <Box className="rt-select-cell" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => toggleRow(id)}
                      sx={{ mt: -0.5 }}
                    />
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {secondaryCol && (
                    <Typography
                      variant="caption"
                      color="primary.main"
                      sx={{ fontFamily: 'monospace', fontWeight: 700, display: 'block' }}
                    >
                      {secondaryCol.render ? secondaryCol.render(row) : row[secondaryCol.key]}
                    </Typography>
                  )}
                  {primaryCol && (
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.3 }}>
                      {primaryCol.render ? primaryCol.render(row) : row[primaryCol.key]}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, rowGap: 0.5 }}>
                    {otherCols.map((col) => {
                      const val = col.render ? col.render(row) : row[col.key];
                      if (val === null || val === undefined || val === '' || val === '—') return null;
                      return (
                        <Box key={col.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          {typeof val === 'string' || typeof val === 'number' ? (
                            <>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                {col.label}:
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.72rem' }}>
                                {val}
                              </Typography>
                            </>
                          ) : (
                            val
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
                {onRowClick && !selectable && <ChevronRightIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />}
              </Box>
            </Paper>
          );
        })}
      </Stack>
    );
  }

  // ── Desktop table layout ────────────────────────────────────────────────
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size={density} stickyHeader>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            {selectable && (
              <TableCell padding="checkbox" sx={{ bgcolor: 'grey.100' }}>
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': 'select all rows' }}
                />
              </TableCell>
            )}
            {columns.map((col) => (
              <TableCell
                key={col.key}
                align={col.align || 'left'}
                sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                sortDirection={sortBy === col.key ? sortDir : false}
              >
                {col.sortable && onSortChange ? (
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortDir : 'asc'}
                    onClick={() => handleSort(col)}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const id = rowKey(row);
            const isSelected = selectable && selectedSet.has(String(id));
            return (
              <TableRow
                key={id}
                hover={!!onRowClick}
                selected={isSelected}
                onClick={(e) => {
                  if (e.target && e.target.closest && e.target.closest('.rt-select-cell')) return;
                  if (onRowClick) onRowClick(row);
                }}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {selectable && (
                  <TableCell padding="checkbox" className="rt-select-cell" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => toggleRow(id)}
                      inputProps={{ 'aria-label': `select row ${id}` }}
                    />
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell key={col.key} align={col.align || 'left'}>
                    {col.render ? col.render(row) : row[col.key]}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
